import { Component, EventEmitter, Input, OnInit, Output, signal, WritableSignal } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { SelectedComponent } from "../selected/selected.component";
import { InputComponent } from "../input/input.component";
import { CommonModule } from '@angular/common';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { ListsService } from '../../services/lists.service';

@Component({
  selector: 'app-automatic-debit',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    SelectedComponent,
    InputComponent
  ],
  templateUrl: './automatic-debit.component.html',
  styleUrl: './automatic-debit.component.css'
})
export class AutomaticDebitComponent implements OnInit {
  
  form: FormGroup;
  isTimeout: boolean = false;
  showDebitDetails: boolean = false;
  private previousAutomaticDebitValue: boolean | null = null;

  dataInputs: WritableSignal<any[]> = signal([
    {
      label: 'Número de Cuenta', 
      subLabel: '', 
      formName: 'accountNumber', 
      placeholder: '', 
      type: 'text', 
      defaultValue: '', 
      enabled: false, 
      validation: [Validators.required],
      textError: 'Por favor ingrese un número de cuenta válido'
    },
    { 
      label: 'Día Débito', 
      subLabel: '', 
      formName: 'debitDay', 
      placeholder: '', 
      type: 'number', 
      defaultValue: '', 
      enabled: false, 
      validation: [Validators.required, Validators.min(1), Validators.max(31)],
      textError: 'Por favor ingrese un día entre 1 y 31'
    }
  ])

  dataSelected: WritableSignal<any[]> = signal([
    { 
      label: 'Seleccione el tipo de débito:', 
      subLabel: '', 
      formName: 'debitType', 
      placeholder: '', 
      type: 'text', 
      defaultValue: '', 
      enabled: false,
      validation: [Validators.required], 
      textError: 'Por favor seleccione un tipo de débito', 
      options: signal([
        { value: 'INTERNO', label: 'Débito Interno' },
        { value: 'EXTERNO', label: 'Débito Externo' }
      ])
    },
    { 
      label: 'Banco a Debitar', 
      subLabel: '', 
      formName: 'bank', 
      placeholder: '', 
      type: 'text', 
      defaultValue: '', 
      enabled: false, 
      validation: [Validators.required],
      textError: 'Por favor seleccione un banco', 
      options: signal([]) 
    },
    { 
      label: 'Tipo de Cuenta', 
      subLabel: '', 
      formName: 'accountType', 
      placeholder: '', 
      type: 'text', 
      defaultValue: '', 
      enabled: false, 
      validation: [Validators.required],
      textError: 'Por favor seleccione un tipo de cuenta', 
      options: signal([
        { value: 'Ahorros', label: 'Ahorros' },
        { value: 'Corriente', label: 'Corriente' }
      ])
    }
  ])
  
  @Output() formValueChange: EventEmitter<any> = new EventEmitter<any>();
  @Input() defaultValue: any;

  constructor(
    private _listService: ListsService
  ){
    this.form = new FormGroup({
      automaticDebit: new FormControl(null, [Validators.required]),
      debitType: new FormControl(null),
      bank: new FormControl(null),
      accountNumber: new FormControl(null),
      accountType: new FormControl(null),
      debitDay: new FormControl(null)
    });

    this.form.valueChanges.subscribe(() => {
      this.emitForm()
    });

    this.form.get('debitType')?.valueChanges.subscribe((value) => {
      if (value === 'EXTERNO') {
        this.enableExternalFields();
      } else {
        this.disableExternalFields();
      }
    });

    this.form.get('automaticDebit')?.valueChanges.subscribe((value) => {
      this.setAutomaticDebit();
    });
  }

  ngOnInit(): void {
    this.getAllBanks();
    console.log("LLEGA: ",this.defaultValue);

    if (this.defaultValue) {
      this.form.get('automaticDebit')?.setValue(this.defaultValue?.automaticDebit);
      this.dataInputs.update((inputs:any) =>
        inputs.map((input:any) => ({
          ...input,
          defaultValue: this.defaultValue[input.formName] ?? input.defaultValue
        }))
      );    
      this.dataSelected.update((inputs:any) =>
        inputs.map((input:any) => ({
          ...input,
          defaultValue: this.defaultValue[input.formName] ?? input.defaultValue
        }))
      );
    }
    
    if (this.defaultValue) {
      this.form.patchValue(this.defaultValue);
      this.previousAutomaticDebitValue = this.defaultValue.automaticDebit;
      
      if (this.defaultValue.automaticDebit === true) {
        this.showDebitDetails = true;
        this.enableDebitFields();
        
        if (this.defaultValue.debitType === 'EXTERNO') {
          this.enableExternalFields();
        }
      }
    }
  }
  
  setAutomaticDebit(): void {
    const wantsAutomaticDebit = this.form.get('automaticDebit')?.value;
    
    if (wantsAutomaticDebit === true) {
      this.showDebitDetails = true;
      this.enableDebitFields();
    } else {
      this.showDebitDetails = false;
      this.disableAllDebitFields();
      
      this.form.patchValue({
        debitType: '',
        bank: '',
        accountNumber: '',
        accountType: '',
        debitDay: ''
      }, { emitEvent: false });
    }
    
    // Guardar el valor actual para la próxima comparación
    this.previousAutomaticDebitValue = wantsAutomaticDebit;
  }

  private enableDebitFields(): void {
    this.form.get('debitType')?.setValidators(Validators.required);
    this.form.get('debitType')?.updateValueAndValidity();
    this.updateFieldEnabledStatus('debitType', true, 0);
  }

  private disableAllDebitFields(): void {
    ['debitType', 'bank', 'accountNumber', 'accountType', 'debitDay'].forEach(field => {
      this.form.get(field)?.clearValidators();
      this.form.get(field)?.updateValueAndValidity();
      this.updateFieldEnabledStatus(field, false);
    });
  }
  
  getAllBanks(){
    this._listService.getAllBanks().subscribe({
      next: (response:any) => {
        this.dataSelected().at(1)?.options.set(response);
      },
      error: (error:any) => {
        console.error("ERROR: ", error)
      }
    })
  }

  emitForm(){
    const wantsAutomaticDebit = this.form.get('automaticDebit')?.value;
    const debitType = this.form.get('debitType')?.value;
    
    // Si el valor de automaticDebit cambió, emitir inmediatamente
    if (wantsAutomaticDebit !== this.previousAutomaticDebitValue) {
      if (wantsAutomaticDebit === false) {
        this.formValueChange.emit({ 
          automaticDebit: false,
          debitType: null,
          bank: null,
          accountNumber: null,
          accountType: null,
          debitDay: null
        });
      } else if (wantsAutomaticDebit === true) {
        // Cuando cambia a true, emitir solo el automaticDebit
        this.formValueChange.emit({ 
          automaticDebit: true,
          debitType: null,
          bank: null,
          accountNumber: null,
          accountType: null,
          debitDay: null 
        });
      }
      this.previousAutomaticDebitValue = wantsAutomaticDebit;
      return;
    }
    
    // Caso 1: No quiere débito automático
    if (wantsAutomaticDebit === false) {
      this.formValueChange.emit({ automaticDebit: false });
      return;
    }
    
    // Caso 2: Quiere débito automático pero no ha seleccionado tipo
    if (wantsAutomaticDebit === true && !debitType) {
      // Emitir solo el automaticDebit para mantener el estado
      this.formValueChange.emit({ automaticDebit: true });
      return;
    }
    
    // Caso 3: Débito interno - emitir los 2 campos
    if (wantsAutomaticDebit === true && debitType === 'INTERNO') {
      this.formValueChange.emit({
        automaticDebit: true,
        debitType: 'INTERNO',
        bank: null,
        accountNumber: null,
        accountType: null,
        debitDay: null
      });
      return;
    }
    
    // Caso 4: Débito externo - verificar que todos los campos estén válidos
    if (wantsAutomaticDebit === true && debitType === 'EXTERNO') {
        this.formValueChange.emit({ ...this.form.value });
      return;
    }
  }

  handleValue(event: {formName:string, value:string | number}) {
    this.form.get(event.formName)?.setValue(event.value);
  }

  private enableExternalFields(): void {
    this.form.get('bank')?.setValidators(Validators.required);
    this.form.get('accountNumber')?.setValidators(Validators.required);
    this.form.get('accountType')?.setValidators(Validators.required);
    this.form.get('debitDay')?.setValidators([Validators.required, Validators.min(1), Validators.max(31)]);
    
    this.form.get('bank')?.updateValueAndValidity();
    this.form.get('accountNumber')?.updateValueAndValidity();
    this.form.get('accountType')?.updateValueAndValidity();
    this.form.get('debitDay')?.updateValueAndValidity();
    
    this.updateFieldEnabledStatus('bank', true);
    this.updateFieldEnabledStatus('accountNumber', true);
    this.updateFieldEnabledStatus('accountType', true);
    this.updateFieldEnabledStatus('debitDay', true);
  }

  private disableExternalFields(): void {
    this.form.get('bank')?.clearValidators();
    this.form.get('accountNumber')?.clearValidators();
    this.form.get('accountType')?.clearValidators();
    this.form.get('debitDay')?.clearValidators();
    
    this.form.patchValue({
      bank: null,
      accountNumber: null,
      accountType: null,
      debitDay: null
    }, { emitEvent: false });
    
    this.form.get('bank')?.updateValueAndValidity();
    this.form.get('accountNumber')?.updateValueAndValidity();
    this.form.get('accountType')?.updateValueAndValidity();
    this.form.get('debitDay')?.updateValueAndValidity();
    
    this.updateFieldEnabledStatus('bank', false);
    this.updateFieldEnabledStatus('accountNumber', false);
    this.updateFieldEnabledStatus('accountType', false);
    this.updateFieldEnabledStatus('debitDay', false);
  }

  private updateFieldEnabledStatus(formName: string, enabled: boolean, index?: number): void {
    const selectedIndex = index !== undefined ? index : this.dataSelected().findIndex(item => item.formName === formName);
    if (selectedIndex !== -1) {
      const updatedSelected = [...this.dataSelected()];
      updatedSelected[selectedIndex] = { ...updatedSelected[selectedIndex], enabled };
      this.dataSelected.set(updatedSelected);
    }
    
    const inputIndex = this.dataInputs().findIndex(item => item.formName === formName);
    if (inputIndex !== -1) {
      const updatedInputs = [...this.dataInputs()];
      updatedInputs[inputIndex] = { ...updatedInputs[inputIndex], enabled };
      this.dataInputs.set(updatedInputs);
    }
  }
}